import { useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { jwtStorage, setAccessToken } from '@final-commerce/jwt-storage-lib';

const idOf = (token?: string | null): string | null => {
  if (!token) return null;
  try {
    return (jwtDecode(token) as { _id?: string })._id ?? null;
  } catch {
    return null;
  }
};

const LOOP_GUARD_KEY = 'identity-sync-target';

/**
 * Cross-app single-user sync (organization-level app).
 *
 * `jwt_me` is a shared `*.finalpos.com` cookie = the globally logged-in user.
 * When another Final app switches the user, this tab must re-derive as the new
 * one. We compare the shared `jwt_me` `_id` against the id THIS tab rendered
 * with — captured ONCE (a sibling tab or a token refresh may overwrite the
 * shared `accessToken`, so re-reading it as "our" identity would mask a switch)
 * — on every point a change could arrive: focus, tab-visible, cross-tab
 * `storage`, and bfcache wake (`pageshow`, which never fires a storage event).
 *
 * - different `_id` → adopt the now-active token + reload (a plain reload keeps
 *   the existing token; we must adopt first). The new user re-derives on load.
 * - same `_id` (a company switch) → no-op.
 *
 * Guards against loops: do nothing while our own session isn't loaded (`!mine`)
 * or `jwt_me` is absent (`!sharedId`); adopt+reload at most once per target user
 * via a `sessionStorage` sentinel, so a token that doesn't re-derive can't
 * reload forever. Runs only when this tab is already logged in — never disturbs
 * the login flow.
 */
export function useSyncActiveUser(): void {
  const renderedId = useRef<string | null>(null);

  useEffect(() => {
    renderedId.current = idOf(localStorage.getItem('accessToken'));

    const sync = () => {
      if (document.visibilityState === 'hidden') return;
      if (renderedId.current === null) renderedId.current = idOf(localStorage.getItem('accessToken'));
      const mine = renderedId.current;
      if (!mine) return; // our session isn't loaded yet — never act on a half-loaded state

      const shared = Object.values(jwtStorage.me)[0]?.jwt;
      const sharedId = idOf(shared);
      if (!sharedId) return; // jwt_me not loaded / logged out — don't churn
      if (sharedId === mine) {
        sessionStorage.removeItem(LOOP_GUARD_KEY); // in sync (company switch counts) — reset the guard
        return;
      }
      if (sessionStorage.getItem(LOOP_GUARD_KEY) === sharedId) return; // already tried — avoid a loop

      sessionStorage.setItem(LOOP_GUARD_KEY, sharedId);
      setAccessToken(shared as string); // adopt the now-active user, then re-derive on reload
      window.location.reload();
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'accessToken') sync();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) sync(); // restored from bfcache — never received the storage event
    };

    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
